import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import type { InventoryItemView } from '@/lib/api-admin/types';
import { InlineExpandRow, PreviewCard, Button, Input } from '@/components/ui';
import { useInventoryAdjustMutation } from '../hooks/useInventoryAdjustMutation';

interface Props {
  item: InventoryItemView;
  onClose: () => void;
}

export const InventoryAdjustExpand = ({ item, onClose }: Props) => {
  const { t } = useTranslation('inventory');
  const [selectedLocation, setSelectedLocation] = useState<string>(() => {
    const keys = Object.keys(item.qtyByLocation);
    return keys[0] ?? '';
  });
  const [qty, setQty] = useState<string>('');
  const [reason, setReason] = useState('');
  const adjust = useInventoryAdjustMutation();

  const currentQty = item.qtyByLocation[selectedLocation] ?? 0;
  const qtyNum = parseInt(qty, 10);
  const newQty = isNaN(qtyNum) ? currentQty : currentQty + qtyNum;
  const delta = isNaN(qtyNum) ? undefined : qtyNum;

  const handleConfirm = () => {
    if (isNaN(qtyNum) || qtyNum === 0) return;
    adjust.mutate(
      { inventoryId: item.inventoryId, qty: qtyNum, ...(reason ? { reason } : {}) },
      { onSuccess: onClose },
    );
  };

  const listContent = (
    <div className="flex flex-col gap-1">
      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--content-muted)' }}>{t('inventory.adjust.byLocation')}</p>
      {Object.entries(item.qtyByLocation).map(([loc, q]) => (
        <button
          key={loc}
          onClick={() => setSelectedLocation(loc)}
          className="flex items-center justify-between rounded px-3 py-2 text-sm text-left transition-colors"
          style={{
            background: selectedLocation === loc ? 'var(--badge-info-bg)' : 'var(--surface-1)',
            color: 'var(--content-strong)',
            border: selectedLocation === loc ? '1px solid var(--badge-info-fg)' : '1px solid var(--divider-soft)',
          }}
        >
          <span>{loc}</span>
          <span className="font-semibold">{q}</span>
        </button>
      ))}
    </div>
  );

  const inputContent = (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>{t('inventory.adjust.title')}</p>
      <div>
        <label className="text-xs mb-1 block" style={{ color: 'var(--content-muted)' }}>
          {t('inventory.adjust.qtyLabel')}
        </label>
        <Input
          type="number"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          placeholder={t('inventory.adjust.qtyPlaceholder')}
        />
      </div>
      <div>
        <label className="text-xs mb-1 block" style={{ color: 'var(--content-muted)' }}>
          {t('inventory.adjust.reasonLabel')}
        </label>
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('inventory.adjust.reasonPlaceholder')}
        />
      </div>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={onClose}>{t('inventory.adjust.cancel')}</Button>
        <Button
          size="sm"
          onClick={handleConfirm}
          disabled={adjust.isPending || isNaN(qtyNum) || qtyNum === 0}
        >
          {adjust.isPending ? t('inventory.adjust.submitting') : t('inventory.adjust.confirm')}
        </Button>
      </div>
    </div>
  );

  const previewContent = (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-semibold" style={{ color: 'var(--content-muted)' }}>{t('inventory.adjust.preview')}</p>
      <PreviewCard
        label={selectedLocation}
        from={currentQty}
        to={newQty}
        {...(delta !== undefined ? { delta } : {})}
      />
    </div>
  );

  return (
    <InlineExpandRow
      list={listContent}
      input={inputContent}
      preview={previewContent}
    />
  );
};
